import React, { FC } from 'react';
import useBoolean from 'utils/hooks/useBoolean';
import WidgetsSetupModal from 'components/posts/detail/layout/widgetsSetup/WidgetsSetupModal';
import { WidgetVariant } from 'app/modules/posts/components/accordionWidget/constants/widgetVariant';
import { useUser } from 'modules/user/userSelector';

export type WidgetsSetupContextValue = {
  setupModalOpen: boolean;
  setSetupModalOpen: ReturnType<typeof useBoolean>[1];
  widgetsSetup: WidgetsSetup[] | null;
  defaultWidgetsSetup: WidgetsSetup[];
  canSetupWidgets: boolean;
  isLoading?: boolean;
  setWidgetExpanded: (key: WidgetVariant, isExpanded: boolean) => void;
};

const WidgetsSetupContext = React.createContext<
  WidgetsSetupContextValue | undefined
>(undefined);

export const useWidgetsSetup = () => {
  const context = React.useContext(WidgetsSetupContext);

  if (!context) {
    throw new Error(
      'useWidgetsSetup must be used within a WidgetsSetupProvider',
    );
  }

  return context;
};

type Props = {
  children: React.ReactNode;
  widgetsSetup?: WidgetsSetup[] | null;
  defaultWidgetsSetup: WidgetsSetup[];
  onWidgetsSetupChange: (setup: WidgetsSetup[] | null) => void;
  isLoading?: boolean;
};

export type WidgetsSetup = {
  key: WidgetVariant;
  isExpanded?: boolean;
};

const ROLES_WITHOUT_WIDGETS_SETUP = ['media_agency', 'client'];

const WidgetsSetupProvider: FC<Props> = ({
  widgetsSetup = null,
  defaultWidgetsSetup,
  onWidgetsSetupChange,
  children,
  isLoading,
}) => {
  const user = useUser();
  const [setupModalOpen, setSetupModalOpen] = useBoolean(false);

  function submitSetup(newWidgetsSetup: WidgetsSetup[] | null) {
    setSetupModalOpen.off();
    onWidgetsSetupChange?.(newWidgetsSetup);
  }

  const setWidgetExpanded = (key: WidgetVariant, isExpanded: boolean) => {
    const currentSetup = widgetsSetup ?? defaultWidgetsSetup;
    const newSetup = currentSetup.map((widget) =>
      widget.key === key ? { ...widget, isExpanded } : widget,
    );
    onWidgetsSetupChange?.(newSetup);
  };

  return (
    <WidgetsSetupContext.Provider
      value={{
        canSetupWidgets: !ROLES_WITHOUT_WIDGETS_SETUP.includes(user.role),
        setupModalOpen,
        setSetupModalOpen,
        widgetsSetup,
        defaultWidgetsSetup,
        isLoading,
        setWidgetExpanded,
      }}
    >
      {children}
      <WidgetsSetupModal
        activeWidgetsSetup={widgetsSetup}
        defaultWidgetsSetup={defaultWidgetsSetup}
        open={setupModalOpen}
        onClose={setSetupModalOpen.off}
        onWidgetsSetupSubmit={submitSetup}
      />
    </WidgetsSetupContext.Provider>
  );
};

export default WidgetsSetupProvider;
